<script>
  export let buildingColor;
  export let buildingPopupColor;
  export let buildingBgColor;
  export let buildingHoverColor;
  export let popupBgColor;
  let buildingHeight = 10;
  
  import { getContext, onMount } from "svelte";
  import { contextKey } from "@beyonk/svelte-mapbox";
  import BuildingPopup from './BuildingPopup.svelte';
  import { fade, fly } from 'svelte/transition';
  import { buildings as allBuildings } from './buildings';
  const buildings = allBuildings.filter((b) => !b.hidden);
  
  const { getMap, getMapbox } = getContext(contextKey);
  const { open } = getContext('simple-modal');
  const map = getMap();
  const tilesetUrl = 'mapbox://zdb-.clifg46120cqo2iqs42bhgle9-3gyth'
  const tilesetLayerName = 'toledo_buildings'
  const labelsUrl = 'mapbox://zdb-.cliq2chd30yqd2ap7hod5a24y-9rnij'
  const labelsLayerName = 'toledo-buildings-labels'

  const buildingPopups = buildings.filter((b) => b.popup);
  const buildingPopupIds = buildingPopups.map((b) => b.id);
  const buildingsWithoutPopups = buildings.filter((b) => !b.popup);
  const buildingsWithoutPopupsIds = buildingsWithoutPopups.map((b) => b.id);
  const buildingIds = buildings.map((i) => i['id']);

  let tooltipX = 0;
  let tooltipY = 0;
  let tooltipText;

  const popupOptions = {
      closeButton: true,
      closeOnEsc: true,
      closeOnOuterClick: true,
      transitionBg: fade,
      transitionBgProps: {
          duration: 250
      },
      transitionWindow: fly,
      transitionWindowProps: {
          y: window.innerHeight,
          duration: 1000
      },
      styleWindowWrap: { alignItems: 'center' },
      styleWindow: {  background: popupBgColor, color: '#fff', height: 'fit-content'   },
      styleContent: {},
      styleCloseButton: { top: '-5px', right: '-5px' }
  };

  // Define layers with their properties
  const layers = [
    { id: 'tour-buildings-layer', color: buildingColor, ids: buildingsWithoutPopupsIds, showLabels: false },
    { id: 'tour-popup-buildings-layer', color: buildingPopupColor, ids: buildingPopupIds, showLabels: false },
    // { id: 'tour-bg-buildings-layer', color: buildingBgColor, ids: buildingIds, exclude: true },
  ];

  const addLayer = (layer) => {
    const filter = layer.exclude 
      ? ['!', ['in', ['get','@id'], ['literal', layer.ids]]] 
      : ['in', ['get','@id'], ['literal', layer.ids]];

    map.addLayer({
        id: layer.id,
        'type': 'fill-extrusion',
        'source': 'base-buildings',
        'source-layer': tilesetLayerName,
        'paint': {
            'fill-extrusion-color': layer.color,
            'fill-extrusion-height': buildingHeight,
        },
        filter,
    });

    if (layer.showLabels){
      // create label id lookup like { 'someid': 'label', 'someid2': 'label2'}
      const buildingLabels = buildings.reduce((acc, cur) => {
        if (cur.id && cur.title) {
          acc[cur.id] = cur.title;
        }
        return acc;
      }, {});

      // Building labels
      map.addLayer({
        id: layer.id + '-labels',
        type: 'symbol',
        source: 'labels-buildings',
        'source-layer': labelsLayerName,
        layout: {
          'text-field': ['get', ['get', '@id'], ['literal', buildingLabels]], // Use the buildingLabels object to map IDs to labels
          'text-size': 16, // Adjust the font size as needed
          'text-allow-overlap': false, // Prevent label overlap
          'text-optional': true, // Labels are optional and won't be placed on top of other labels
        },
        paint: {
          'text-color': '#000', // Adjust the label color
          'text-halo-color': '#fff', // Add a white halo around the text
          'text-halo-width': 2, // Adjust the halo width
        },
        filter,
        minzoom: 15,
      });
    }

  }

  onMount(async () => {
    // Logs coordinates of click to console
    map.on("click", function(e){
        console.log(e.lngLat);
    });

    // Logs Building ID to console
    map.on("click", "tour-bg-buildings-layer", function(e){
        if(e.features && e.features[0] && e.features[0].properties){
            console.log(e.features[0].properties['@id']);
        }
    });

    // Click Event on Building
    map.on("click", "tour-popup-buildings-layer", function (e) {
        if(!e.features){ return; }
        let id = e.features[0].properties['@id'];
        let buildingPopup = buildingPopups.filter((popup)=>{
            return popup.id === id;
        })
        if(buildingPopup.length > 0){
            let popup = buildingPopup[0].popup;
            console.log(popup);
            open(BuildingPopup, popup, popupOptions);
            // remove highlight and hover styles
            resetHoverStyle();
        } else {
            console.log(id);
            console.log(e.features[0].properties)
        }
    });

    map.addSource('base-buildings', {
        type: 'vector',
        url: tilesetUrl,
    });

    map.addSource('labels-buildings', {
        type: 'vector',
        url: labelsUrl,
    });

    layers.forEach(addLayer);

    // Reset hover styles for all layers
    const resetHoverStyle = () => {
      layers.forEach(layer => map.setPaintProperty(layer.id, "fill-extrusion-color", layer.color));
    };
    
    // Building Hover
    let animatedBuildingId = null;

    map.on('mousemove', function(e) {
      const layerIds = layers.map(layer => layer.id);
      const featuresUnderMouse = map.queryRenderedFeatures(e.point, { layers: layerIds });

      if (!featuresUnderMouse.length) {
          resetHoverStyle();
          tooltipText = false;
          map.getCanvas().style.cursor = "";  // Change cursor back to default
          if(animatedBuildingId !== null){
              // Reset height of the last animated building when mouse is not on any building
              map.setPaintProperty(
                  'tour-buildings-layer', 
                  "fill-extrusion-height", 
                  ['match', ['get', '@id'], animatedBuildingId, buildingHeight, buildingHeight]
              );
              animatedBuildingId = null;
          }
          return;
      }
      
      const hoveredFeature = featuresUnderMouse[0];
      const hoveredFeatureId = hoveredFeature.properties['@id'];
      const hoveredBuilding = buildings.find(b => b.id === hoveredFeatureId);
      tooltipText = hoveredBuilding ? hoveredBuilding.title : hoveredFeature.properties['name'];
      tooltipX = e.point.x;
      tooltipY = e.point.y;
      const hoveredLayerId = hoveredFeature.layer.id;
      const nonHoveredColor = layers.find(layer => layer.id === hoveredLayerId).color;

      map.setPaintProperty(
          hoveredLayerId,
          "fill-extrusion-color", 
          ['match', ['get', '@id'], hoveredFeatureId, buildingHoverColor, nonHoveredColor]
      );

      if (animatedBuildingId !== hoveredFeatureId) {
          animatedBuildingId = hoveredFeatureId;

          // Start the animation
          let startHeight = buildingHeight;
          let endHeight = startHeight * 5;
          let duration = 500; // Duration of the animation in milliseconds
          let startTime = performance.now();

          function animateHeight(timestamp) {
              let elapsedTime = timestamp - startTime;
              let progress = elapsedTime / duration;
              let currentHeight = startHeight + (endHeight - startHeight) * progress;

              if(animatedBuildingId === hoveredFeatureId) {
                  map.setPaintProperty(
                      hoveredLayerId, 
                      "fill-extrusion-height",
                      ['match', ['get', '@id'], animatedBuildingId, currentHeight, startHeight]
                  );
                  if (elapsedTime < duration) {
                      requestAnimationFrame(animateHeight);
                  }
              } else if (animatedBuildingId === null){
                  map.setPaintProperty(
                      hoveredLayerId, 
                      "fill-extrusion-height", 
                      ['match', ['get', '@id'], hoveredFeatureId, buildingHeight, buildingHeight]
                  );
              }
          }

          requestAnimationFrame(animateHeight);
      }

      map.getCanvas().style.cursor = "pointer";  // Change cursor to pointer
    });
});
</script>

<style>
    :root {
        --tooltip-background: #000;
        --tooltip-color: #fff;
        --tooltip-padding: 0.5rem;
        --tooltip-border-radius: 15px;
    }

    .tooltip {
        position: absolute;
        background: var(--tooltip-background);
        color: var(--tooltip-color);
        padding: var(--tooltip-padding);
        z-index: 999;
        border-radius: var(--tooltip-border-radius);
    }
</style>

{#if tooltipText}
<div class="tooltip" style={` top: ${tooltipY-50}px; left: ${tooltipX-50}px;`}>{tooltipText}</div>
{/if}